import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEnvelope, FaSpinner, FaArrowLeft, FaShieldAlt, FaBuilding, FaMobileAlt, FaUsers, FaCheckCircle } from "react-icons/fa";
import axios from "axios";
import { api } from "../utils/apiUtils";
import { API_URL } from "../config/apiConfig";

const CorporateRegister = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState("email");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [totalEmployees, setTotalEmployees] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoImages, setPromoImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch corporate register promotional images
  useEffect(() => {
    const fetchPromoImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'corporate_register' }
        });
        if (response.data.success && response.data.data.length > 0) {
          setPromoImages(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching corporate register images:", error);
      }
    };
    fetchPromoImages();
  }, []);

  // Auto-rotate images
  useEffect(() => {
    if (promoImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % promoImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [promoImages]);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (!companyName) { toast.error("Please enter company name"); return; }
    if (!email) { toast.error("Please enter company email"); return; }
    if (!mobileNumber) { toast.error("Please enter mobile number"); return; }
    const emp = Number(totalEmployees);
    if (!Number.isFinite(emp) || emp < 0) { toast.error("Please enter valid number of employees"); return; }

    setLoading(true);
    try {
      const res = await api.post("/corporate/auth/register/send-otp", {
        companyName,
        email,
        mobileNumber,
        totalEmployees: emp,
      });
      if (res?.data?.success) {
        toast.success(res.data.message || "OTP sent");
        setStep("otp");
      } else {
        toast.error(res?.data?.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!otp) { toast.error("Please enter OTP"); return; }

    setLoading(true);
    try {
      const res = await api.post("/corporate/auth/register/verify-otp", { email, otp });
      if (res?.data?.success) {
        toast.success(res.data.message || "Registration successful");
        setStep("done");
      } else {
        toast.error(res?.data?.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setOtp("");
    setStep("email");
  };

  return (
    <div className="min-h-screen bg-[#F2F2F2] flex items-center justify-center p-2 sm:p-4 py-6 sm:py-12">
      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl sm:shadow-2xl w-full max-w-[1100px] flex flex-col md:flex-row overflow-hidden relative min-h-0 md:min-h-[650px]">
        {/* Left Side - Promo Image */}
        <div className="hidden md:block w-[40%] relative bg-[#F56753] overflow-hidden">
          {promoImages.length > 0 ? (
            <div className="absolute inset-0">
              <img
                src={promoImages[currentImageIndex].imageUrl}
                alt={promoImages[currentImageIndex].title}
                className="w-full h-full object-cover transition-opacity duration-1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-12 left-10 right-10 text-white">
                <h3 className="text-3xl font-black mb-4 leading-tight">
                  {promoImages[currentImageIndex].title}
                </h3>
                <p className="text-sm opacity-90 leading-relaxed font-medium">
                  {promoImages[currentImageIndex].description}
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <FaSpinner className="animate-spin text-white text-4xl" />
            </div>
          )}
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-[60%] p-5 pt-16 pb-6 sm:p-10 md:p-14 flex flex-col justify-center relative">
          <button 
            onClick={() => navigate("/corporate/login")}
            className="absolute top-5 left-5 text-gray-400 hover:text-[#F56753] transition-colors flex items-center gap-1.5 text-xs sm:text-sm font-bold"
          >
            <FaArrowLeft /> BACK TO LOGIN
          </button>

          <div className="mb-6 md:mb-10 text-center md:text-left mt-4 md:mt-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-2 md:mb-3 tracking-tight">Corporate Registration</h1>
            <p className="text-gray-500 font-bold tracking-wide uppercase text-[9px] sm:text-[10px]">Empower your business travel with CabZigO</p>
          </div>

          {step === "email" && (
            <form onSubmit={sendOtp} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 md:gap-x-8 gap-y-5 md:gap-y-8 animate-fadeIn">
              <div className="group">
                <label className="block text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-[#F56753] transition-colors">
                  Company Name
                </label>
                <div className="relative border-b-2 border-gray-100 group-focus-within:border-[#F56753] transition-all duration-300 pb-1.5 md:pb-2">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#F56753] transition-colors">
                    <FaBuilding size={18} />
                  </div>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full pl-8 py-2 bg-transparent text-base md:text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200"
                    placeholder="e.g. Acme Corp"
                    required
                  />
                </div>
              </div>

              <div className="group">
                <label className="block text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-[#F56753] transition-colors">
                  Corporate Email
                </label>
                <div className="relative border-b-2 border-gray-100 group-focus-within:border-[#F56753] transition-all duration-300 pb-1.5 md:pb-2">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#F56753] transition-colors">
                    <FaEnvelope size={18} />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-8 py-2 bg-transparent text-base md:text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200"
                    placeholder="admin@acme.com"
                    required
                  />
                </div>
              </div>

              <div className="group">
                <label className="block text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-[#F56753] transition-colors">
                  Mobile Number
                </label>
                <div className="relative border-b-2 border-gray-100 group-focus-within:border-[#F56753] transition-all duration-300 pb-1.5 md:pb-2">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#F56753] transition-colors">
                    <FaMobileAlt size={18} />
                  </div>
                  <input
                    type="tel"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    className="w-full pl-8 py-2 bg-transparent text-base md:text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200"
                    placeholder="0000000000"
                    required
                  />
                </div>
              </div>

              <div className="group">
                <label className="block text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[2px] mb-3 px-1 group-focus-within:text-[#F56753] transition-colors">
                  Total Employees
                </label>
                <div className="relative border-b-2 border-gray-100 group-focus-within:border-[#F56753] transition-all duration-300 pb-1.5 md:pb-2">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-[#F56753] transition-colors">
                    <FaUsers size={18} />
                  </div>
                  <input
                    type="number"
                    value={totalEmployees}
                    onChange={(e) => setTotalEmployees(e.target.value)}
                    className="w-full pl-8 py-2 bg-transparent text-base md:text-lg font-bold text-gray-800 focus:outline-none placeholder:text-gray-200"
                    placeholder="Number of members"
                    required
                  />
                </div>
              </div>

              <div className="md:col-span-2 pt-2 md:pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`w-full py-3.5 md:py-5 rounded-full font-black text-base md:text-lg tracking-[2px] shadow-xl transition-all duration-300 active:scale-95 ${
                    loading
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-[#F56753] to-[#d94e3a] text-white hover:shadow-red-200/50 hover:shadow-2xl"
                  }`}
                >
                  {loading ? <FaSpinner className="animate-spin mx-auto text-2xl" /> : "SEND OTP"}
                </button>
              </div>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={verifyOtp} className="max-w-md mx-auto w-full space-y-6 md:space-y-10 animate-fadeIn">
              <div className="bg-red-50/50 p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-red-100 flex items-start gap-3 sm:gap-4">
                <div className="mt-1 text-[#F56753]"><FaShieldAlt size={18} className="sm:w-[22px] sm:h-[22px]" /></div>
                <div>
                  <p className="text-xs sm:text-sm font-bold text-red-900 mb-1">OTP Sent Successfully</p>
                  <p className="text-[11px] sm:text-xs text-red-700 leading-relaxed">
                    We've sent a 6-digit code to <span className="font-black underline">{email}</span> for <strong>{companyName}</strong>.
                  </p>
                </div>
              </div>

              <div className="group">
                <label className="block text-[10px] md:text-[11px] font-black text-gray-400 uppercase tracking-[2px] mb-3 md:mb-5 text-center">
                  Verification Code
                </label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full text-center text-2xl sm:text-4xl md:text-5xl font-black tracking-[8px] sm:tracking-[12px] md:tracking-[15px] py-2 md:py-4 border-b-2 border-gray-100 focus:border-[#F56753] focus:outline-none transition-all duration-300 text-gray-800"
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  required
                />
              </div>

              <div className="flex flex-col gap-4 md:gap-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 md:py-5 rounded-full bg-gradient-to-r from-[#F56753] to-[#d94e3a] text-white font-black text-base md:text-lg tracking-[2px] shadow-xl shadow-red-200 transition-all duration-300 active:scale-95"
                >
                  {loading ? <FaSpinner className="animate-spin mx-auto text-2xl" /> : "COMPLETE REGISTRATION"}
                </button>
                <button
                  type="button"
                  onClick={reset}
                  className="text-[10px] md:text-[11px] font-black text-red-400 tracking-widest uppercase hover:text-[#F56753] transition-colors text-center"
                >
                  Edit Information
                </button>
              </div>
            </form>
          )}

          {step === "done" && (
            <div className="max-w-md mx-auto w-full text-center space-y-6 md:space-y-8 animate-fadeIn">
              <div className="flex justify-center">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-50 rounded-full flex items-center justify-center text-green-500 shadow-inner">
                  <FaCheckCircle className="text-4xl sm:text-5xl" />
                </div>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 mb-2 md:mb-4 tracking-tight">Successfully Submitted!</h2>
                <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-medium">
                  Registration for <span className="font-black text-gray-800">{companyName}</span> has been received. Our team will review your application and provide access shortly.
                </p>
              </div>
              <div className="bg-orange-50 p-4 sm:p-6 rounded-2xl border border-orange-100">
                <p className="text-[10px] sm:text-[11px] font-black text-orange-800 uppercase tracking-widest leading-loose">
                  Pending Admin Approval
                </p>
              </div>
              <button
                onClick={() => navigate("/corporate/login")}
                className="w-full py-3.5 md:py-5 rounded-full bg-gray-900 text-white font-black tracking-[2px] hover:bg-black transition-all duration-300 shadow-xl"
              >
                BACK TO LOGIN
              </button>
            </div>
          )}

          <div className="mt-8 md:mt-14 text-center">
            <p className="text-[9px] md:text-[10px] text-gray-400 font-bold leading-relaxed uppercase tracking-widest">
              Secured Enterprise Registration Portal • CabZigO © 2024
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorporateRegister;
